import { readLocale, translateUi, type Locale } from "./i18n.ts";
import { ApiRequestError } from "./api-read-retry.ts";

export function parseApiJson(text: string): unknown {
  if (!text) return null;
  try { return JSON.parse(text); }
  catch { return null; }
}

export function apiErrorMessage(status: number, data: unknown, locale: Locale = readLocale()): string {
  if (data && typeof data === "object" && "error" in data && typeof data.error === "string" && data.error.trim()) {
    if (!/^\s*<(?:!doctype\s+html|html|body|h1)\b/i.test(data.error)) return data.error;
  }
  if (status === 429) return translateUi("Shërbimi po merr shumë kërkesa. Prit pak dhe provo përsëri.", "The service is receiving too many requests. Wait a moment and try again.", locale);
  return translateUi("Kërkesa dështoi. Provo përsëri.", "The request failed. Try again.", locale);
}

export const busyApiMessage = apiErrorMessage(429, null);

export function createApiReadCooldown(clock?: () => number) {
  const now = clock ?? (() => performance.now());
  const wallNow = clock ?? Date.now;
  let hostUntil = 0;
  let hostError: ApiRequestError | undefined;
  const paths = new Map<string, { until: number; error?: ApiRequestError }>();
  return {
    check(path: string) {
      for (const [key, entry] of paths) if (entry.until <= now()) paths.delete(key);
      if (hostUntil > now()) throw hostError ?? new ApiRequestError(apiErrorMessage(429, null), 429);
      const entry = paths.get(path);
      if (entry && entry.until > now()) throw entry.error ?? new ApiRequestError(apiErrorMessage(429, null), 429);
    },
    record(path: string, retryAfter: string | null, hostLimited: boolean, error?: ApiRequestError) {
      const seconds = retryAfter?.trim() ? Number(retryAfter) : NaN;
      const date = retryAfter && !Number.isFinite(seconds) ? Date.parse(retryAfter) : NaN;
      const delay = Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000
        : Number.isFinite(date) ? Math.max(0, date - wallNow()) : 30_000;
      const until = now() + Math.max(1000, delay);
      if (hostLimited) { hostUntil = Math.max(hostUntil, until); hostError = error; }
      else paths.set(path, { until: Math.max(paths.get(path)?.until ?? 0, until), error });
    },
  };
}
